import * as React from "react";
import type { Omittable } from "../lib/props";

/**
 * CSS selector matching the wrapper elements that portaled overlays (Base UI
 * popovers, menus, selects, dialogs) attach to `document.body`. Used by
 * `useOutsideClick` so a click inside a portaled menu/listbox isn't treated as
 * "outside" the cell that opened it.
 *
 * Base UI tags every popup surface with a `data-slot` ending in `-popup`; the
 * ARIA roles catch the rest.
 */
export const PORTALED_OVERLAY_SELECTOR =
  "[data-slot$='-popup'], [role='dialog'], [role='menu'], [role='listbox']";

export type UseOutsideClickOptions = Omittable<{
  /**
   * Element whose subtree counts as "inside." Clicks within this ref are
   * ignored. Typed against `Node["contains"]` so a real element ref
   * (`useRef<HTMLDivElement | null>`) satisfies it — a `(node: unknown)`
   * parameter would be contravariantly incompatible with every DOM node.
   */
  ref: React.RefObject<{ contains?: (node: Node | null) => boolean } | null>;
  /** When false, no listener is attached. */
  active: boolean;
  /** Called when a click lands outside `ref` and outside any portaled overlay. */
  onOutside: (event: MouseEvent) => void;
  /**
   * Optional extra predicate. Return `true` to treat the click as inside
   * (ignore it). Useful for native pickers that fire clicks on `document.body`.
   */
  shouldIgnore?: (target: HTMLElement) => boolean;
}>;

export function useOutsideClick({
  ref,
  active,
  onOutside,
  shouldIgnore,
}: UseOutsideClickOptions): void {
  React.useEffect(() => {
    if (!active) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // The click that opened this layer often lands on an element the open
      // handler then unmounts (e.g. an inline-edit cell swapping its display
      // trigger for an input). React flushes that commit — and this listener's
      // own attach effect — synchronously within the trusted click, so the same
      // click reaches `document` with the trigger already detached. A detached
      // target can't be "inside" any live ref, so the naive contains() check
      // misreads it as an outside click and instantly dismisses. A click whose
      // target left the DOM via its own handler is never an outside click.
      if (!target.isConnected) return;
      if (ref.current?.contains?.(target)) return;
      if (target.closest(PORTALED_OVERLAY_SELECTOR)) return;
      if (shouldIgnore?.(target)) return;
      onOutside(event);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [active, onOutside, ref, shouldIgnore]);
}
